// Full-detail popout for a single deployment history entry — everything the
// compact DeploymentRow doesn't have room for (buildUser, region, full
// deployment ID / plan link, job ID, the auto-rollback/watch bookkeeping block,
// the root-cause analysis and its "Analyse failure" button, and — for an Argo
// deployment — its logs, on a Logs tab beside Details).
// Mirrors DeploymentProgress's own details dialog exactly (same
// Dialog/DialogTitle/Tabs/DialogContent structure, label-over-value Stack
// sections) rather than inventing a new modal style.

import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AccessTime as AccessTimeIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { DeploymentRecord } from '../../types/DeploymentRecord';
import { getColors, rounded } from '../theme/tokens';
import {
  getStatusHue,
  normalizeStatus,
  statusHues,
  statusLabels,
  tagStyle,
} from '../theme/statusTokens';
import { getWatchHue, getWatchLabel, hasWatchState } from '../theme/watchTokens';
import { isDestroyOperation } from '../../utils/serviceParser';
import { formatTimestamp, timeAgo } from '../lib/format';
import { describeErrorInfo } from '../lib/errorFormat';
import TypeBadge from './TypeBadge';
import EnvBadge from './EnvBadge';
import DeploymentLogsView from './DeploymentLogsView';
import DeploymentError from './DeploymentError';
import RcaPanel from './RcaPanel';
import StartRcaButton from './StartRcaButton';
import { canStartRca } from '../lib/rca';

export interface DeploymentDetailsDialogProps {
  record: DeploymentRecord | null;
  onClose: () => void;
}

const humanize = (s?: string): string => (s || '').replace(/_/g, ' ');

/** Label over value — the repeated field pattern from DeploymentProgress's dialog. */
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  return (
    <Box>
      <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: c.ink }}>
        {children}
      </Typography>
    </Box>
  );
};

/** A copy-to-clipboard icon button with the same checkmark-flip feedback used elsewhere. */
const CopyButton: React.FC<{ value: string; label: string }> = ({ value, label }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <Tooltip title={copied ? 'Copied' : label} arrow>
      <IconButton size="small" aria-label={label} onClick={handleCopy} sx={{ color: copied ? statusHues.success : c.inkMuted }}>
        {copied ? <CheckIcon sx={{ fontSize: 15 }} /> : <CopyIcon sx={{ fontSize: 15 }} />}
      </IconButton>
    </Tooltip>
  );
};

const DeploymentDetailsDialog: React.FC<DeploymentDetailsDialogProps> = ({ record, onClose }) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const c = getColors(mode);
  const [view, setView] = useState<'details' | 'logs'>('details');

  // A newly opened record always lands on Details, never a stale Logs tab.
  const recordKey = record?.deployment_id ?? record?.id;
  useEffect(() => {
    setView('details');
  }, [recordKey]);

  if (!record) return null;

  const {
    version,
    status,
    type,
    envName,
    ttl,
    timestamp,
    buildUser,
    region,
    job_id,
    serviceName,
    deployment_id,
    stage,
    step,
    s3_plan_link,
    decision,
    decision_by,
    decision_timestamp,
    error,
    error_info,
    watch_state,
    watch_end_reason,
    watch_concluded_at,
    auto_rollback_opted_in,
    rolled_back,
    rolled_back_from,
    rollback_deployment_id,
    rollback_initiated_by,
    rollback_trigger,
    rollback_reason,
    rollback_decided_at,
    rca,
  } = record;

  const canonical = normalizeStatus(status);
  const destroy = isDestroyOperation(record);
  const failed = canonical === 'failed';
  const startedAgo = timestamp ? timeAgo(timestamp) : null;
  // Why an auto-rollback itself failed (rollback/executor.go) — written onto
  // this, the original deployment's record, when its watch hits rollback_failed.
  const rollbackFailure = describeErrorInfo(error_info);
  const showWatch =
    hasWatchState(watch_state) || rolled_back || !!rollback_reason || !!rollbackFailure;
  // Only Argo deployments publish logs — see DeploymentRow's "View logs" button.
  // The id is what the logs are fetched by, so a record without one would
  // offer a control that can only land on an error.
  const showLogs = record.metadata?.engine === 'argo' && !!record.deployment_id;

  return (
    <Dialog
      open={!!record}
      onClose={onClose}
      maxWidth={showLogs ? 'md' : 'sm'}
      fullWidth
      PaperProps={{ sx: { backgroundColor: c.surface, borderRadius: `${rounded.lg}px` } }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, color: c.ink }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '1.05rem' }}>Deployment details</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: c.inkMuted }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      {showLogs && (
        <Tabs
          value={view}
          onChange={(_, v) => setView(v as 'details' | 'logs')}
          sx={{
            px: 3,
            minHeight: 40,
            borderBottom: `1px solid ${c.hairline}`,
            '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 },
          }}
        >
          <Tab value="details" label="Details" />
          <Tab value="logs" label="Logs" />
        </Tabs>
      )}
      <DialogContent>
        {showLogs && view === 'logs' ? (
          <DeploymentLogsView record={record} enabled={view === 'logs'} />
        ) : (
        <Stack spacing={2}>
          {/* Header — version + status + type/env + destroy tag */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography
              sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '0.95rem',
                fontWeight: 600,
                color: c.ink,
              }}
            >
              {version || '—'}
            </Typography>
            {(() => {
              const tg = tagStyle(getStatusHue(status), mode);
              return (
                <Box
                  component="span"
                  sx={{
                    px: 1,
                    py: 0.3,
                    borderRadius: '6px',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    backgroundColor: tg.bg,
                    color: tg.text,
                    border: `1px solid ${tg.border}`,
                  }}
                >
                  {statusLabels[canonical]}
                </Box>
              );
            })()}
            <TypeBadge type={type} />
            <EnvBadge env={envName} dynamic={ttl != null} />
            {destroy && (
              (() => {
                const tg = tagStyle(statusHues.pending, mode);
                return (
                  <Box
                    component="span"
                    sx={{
                      px: 0.75,
                      py: 0.1,
                      borderRadius: '4px',
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      backgroundColor: tg.bg,
                      color: tg.text,
                      border: `1px solid ${tg.border}`,
                    }}
                  >
                    destroy
                  </Box>
                );
              })()
            )}
          </Box>

          {/* Deployed */}
          {timestamp && (
            <Box>
              <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block', mb: 0.25 }}>
                Deployed
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: c.ink }}>
                <AccessTimeIcon sx={{ fontSize: 15, color: c.inkMuted }} />
                <Typography variant="body2">
                  {startedAgo} · {formatTimestamp(timestamp)}
                </Typography>
              </Stack>
            </Box>
          )}

          {/* Deployed by / Region / Job ID */}
          {buildUser && <Field label="Deployed by">{buildUser}</Field>}
          {region && <Field label="Region">{region}</Field>}
          {job_id && job_id !== serviceName && <Field label="Job ID">{job_id}</Field>}

          {/* Deployment ID */}
          {deployment_id && (
            <Box>
              <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block', mb: 0.25 }}>
                Deployment ID
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: '0.82rem',
                    color: c.ink,
                    wordBreak: 'break-all',
                  }}
                >
                  {deployment_id}
                </Typography>
                <CopyButton value={deployment_id} label="Copy deployment ID" />
              </Stack>
            </Box>
          )}

          {/* Stage / Step — shown unconditionally here, unlike the compact row */}
          {(stage || step) && (
            <Field label="Stage / Step">{[stage, step].filter(Boolean).join(' · ')}</Field>
          )}

          {/* Terraform plan */}
          {s3_plan_link && (
            <Box>
              <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block', mb: 0.25 }}>
                Terraform plan
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: '0.78rem',
                    color: c.ink,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 320,
                  }}
                  title={s3_plan_link}
                >
                  {s3_plan_link}
                </Typography>
                <Tooltip title="Open plan" arrow>
                  <IconButton
                    size="small"
                    component="a"
                    href={s3_plan_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: c.inkMuted }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <CopyButton value={s3_plan_link} label="Copy plan link" />
              </Stack>
            </Box>
          )}

          {/* Approval */}
          {decision && (
            <Field label="Approval">
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  color: decision.toLowerCase() === 'rejected' ? statusHues.failed : statusHues.success,
                }}
              >
                {decision}
              </Box>
              {decision_by ? ` by ${decision_by}` : ''}
              {decision_timestamp ? ` · ${formatTimestamp(decision_timestamp)}` : ''}
            </Field>
          )}

          {/* Auto-rollback / monitor */}
          {showWatch && (
            <Box>
              <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block', mb: 0.75 }}>
                Auto-rollback watch
              </Typography>
              <Stack spacing={0.75}>
                {hasWatchState(watch_state) && (
                  <Box>
                    {(() => {
                      const tg = tagStyle(getWatchHue(watch_state), mode);
                      return (
                        <Box
                          component="span"
                          sx={{
                            px: 1,
                            py: 0.3,
                            borderRadius: '6px',
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            backgroundColor: tg.bg,
                            color: tg.text,
                            border: `1px solid ${tg.border}`,
                          }}
                        >
                          {getWatchLabel(watch_state)}
                        </Box>
                      );
                    })()}
                    {watch_end_reason && (
                      <Typography variant="body2" sx={{ color: c.inkMuted, mt: 0.5 }}>
                        {humanize(watch_end_reason)}
                        {watch_concluded_at ? ` · ${formatTimestamp(watch_concluded_at)}` : ''}
                      </Typography>
                    )}
                  </Box>
                )}
                {auto_rollback_opted_in != null && (
                  <Field label="Auto-rollback opted in">{auto_rollback_opted_in ? 'Yes' : 'No'}</Field>
                )}
                {rolled_back && (
                  <>
                    {rolled_back_from && <Field label="Rolled back from">{rolled_back_from}</Field>}
                    {rollback_deployment_id && (
                      <Field label="Rollback deployment ID">{rollback_deployment_id}</Field>
                    )}
                    {rollback_initiated_by && (
                      <Field label="Rollback initiated by">{rollback_initiated_by}</Field>
                    )}
                    {rollback_trigger && <Field label="Rollback trigger">{humanize(rollback_trigger)}</Field>}
                    {rollback_reason && <Field label="Rollback reason">{rollback_reason}</Field>}
                    {rollback_decided_at && (
                      <Field label="Rollback decided">{formatTimestamp(rollback_decided_at)}</Field>
                    )}
                  </>
                )}
                {rollbackFailure && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: c.inkMuted, display: 'block', mb: 0.25 }}
                    >
                      Rollback failure
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        fontSize: '0.8rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        color: statusHues.failed,
                      }}
                    >
                      {rollbackFailure}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          {/* Error */}
          {failed && error != null && (
            <DeploymentError
              error={error}
              record={record}
              onViewLogs={showLogs ? () => setView('logs') : undefined}
            />
          )}

          {/* Root-cause analysis — the result once there is one, and the
              button to start (or re-run) it on an eligible failure. */}
          {rca && <RcaPanel rca={rca} deploymentId={record.deployment_id} />}
          {canStartRca(record) && (
            <Box>
              <StartRcaButton record={record} variant="button" />
            </Box>
          )}
        </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DeploymentDetailsDialog;
